import {
  PageManager,
  PopupManager,
  SubPopupManager,
} from "accessRoutes/PopupManager";
import { useCheckToken } from "hooks/useCheckToken";
import PublicRoute from "accessRoutes/PublicRoute";
import { LanguagePath } from "components/atoms/LanguagePath/LanguagePath";
import CasinoLayout from "components/layouts/CasinoLayout";
import InfoLayout from "components/layouts/InfoLayout";
import LandingLayout from "components/layouts/LandingLayout";
import PromoLayout from "components/layouts/PromoLayout";
import {
  CASINO_ROUTES,
  INFO_ROUTES,
  LANDING_ROUTES,
  PAGES,
  PROFILE_ROUTES,
  PROMO_ROUTES,
  TOURNAMENT_ROUTES,
} from "constants/navigation/routes";
import PreloaderContainer from "containers/molecules/PreloaderContainer";
import PreloaderLandingContainer from "containers/molecules/PreloaderLandingContainer";
import { useBreakpoint } from "hooks/useBreakpoint";
import { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { Route, Routes, useLocation } from "react-router-dom";
import url from "url/index";
import "@atlaskit/css-reset";
import LandingRoute from "accessRoutes/LandingRoute";
import PrivateRoute from "accessRoutes/PrivateRoute";
import ChatwootWidget from "components/atoms/ChatWootWidget";
import ProfileLayout from "./components/layouts/ProfileLayout";
import TournamentsLayout from "./components/layouts/TournamentsLayout";
import "./theme/main.sass";
import "react-loading-skeleton/dist/skeleton.css";
import * as userSelectors from "./redux/user/selectors";
import { useFeatureFlag } from "./hooks/useFeatureFlag";
import { getEnv } from "./utils/hostname";

const LazyGamePageContainer = lazy(
  () => import("containers/pages/GamePageContainer"),
);
const LazyErrorPageContainer = lazy(
  () => import("containers/pages/ErrorPageContainer"),
);
const LazyHomePageContainer = lazy(
  () => import("containers/pages/HomePageContainer"),
);
const LazyPage404 = lazy(() => import("components/pages/StaticPages/Page404"));

const LazyNotAvailablePage = lazy(
  () => import("components/pages/StaticPages/NotAvailablePage"),
);
const LazyBannedPage = lazy(
  () => import("components/pages/StaticPages/AccountBlockedPage"),
);
const isDev = getEnv() === "dev" || getEnv() === "local";

const App = () => {
  const { isDesktop } = useBreakpoint();
  const location = useLocation();
  const userData = useSelector(userSelectors.all) || "";

  const isLandingRoute = LANDING_ROUTES.some((route) =>
    location.pathname.startsWith(route.url),
  );

  useCheckToken();

  return (
    <>
      <ChatwootWidget />
      {!isLandingRoute ? <PreloaderContainer /> : <PreloaderLandingContainer />}
      <Routes>
        <Route
          element={
            <PublicRoute>
              <CasinoLayout />
            </PublicRoute>
          }
        >
          {/*  CASINO ROUTES  */}
          {CASINO_ROUTES.map((route) => {
            if (route.id === PAGES.HOME) {
              return <Route key={route.id} index element={route.page} />;
            }
            return (
              <Route key={route.id} path={route.url} element={route.page} />
            );
          })}
          {isDesktop && (
            <Route
              path={url.config.casino.game}
              element={
                <Suspense fallback="Loading">
                  <LazyGamePageContainer />
                </Suspense>
              }
            />
          )}
        </Route>

        {/* PROFILE_ROUTES */}
        <Route
          element={
            <PrivateRoute>
              <ProfileLayout />
            </PrivateRoute>
          }
        >
          <Route path={url.config.profile.root}>
            {PROFILE_ROUTES.map((route) => {
              return (
                <Route key={route.id} path={route.url} element={route.page} />
              );
            })}
          </Route>
        </Route>
        {/* TOURNAMENT ROUTES */}
        <Route
          element={
            <PublicRoute>
              <TournamentsLayout />
            </PublicRoute>
          }
        >
          <Route path={url.config.tournament.root}>
            {TOURNAMENT_ROUTES.map((route) => {
              return (
                <Route key={route.id} path={route.url} element={route.page} />
              );
            })}
          </Route>
        </Route>

        {/* CASINO GAME MOBILE */}
        {!isDesktop && (
          <Route
            path={url.config.casino.game}
            element={
              <Suspense fallback="Loading">
                <LazyGamePageContainer />
              </Suspense>
            }
          />
        )}

        {/* LANDING ROUTES */}
        <Route
          element={
            <LandingRoute>
              <LandingLayout />
            </LandingRoute>
          }
        >
          <Route path={url.config.landing.root}>
            {LANDING_ROUTES.map((route) => {
              return (
                <Route key={route.id} path={route.url} element={route.page} />
              );
            })}
          </Route>
        </Route>

        {/* INFO ROUTES */}
        <Route
          element={
            <PublicRoute>
              <InfoLayout />
            </PublicRoute>
          }
        >
          <Route path={url.config.info.root}>
            {INFO_ROUTES.map((route) => {
              return (
                <Route key={route.id} path={route.url} element={route.page} />
              );
            })}
          </Route>
        </Route>

        {/* PROMO ROUTES */}
        <Route
          element={
            <PublicRoute>
              <PromoLayout />
            </PublicRoute>
          }
        >
          <Route path={url.config.promo.root}>
            {PROMO_ROUTES.map((route) => {
              return (
                <Route key={route.id} path={route.url} element={route.page} />
              );
            })}
          </Route>
        </Route>

        {/* SERVICE ERROR */}
        <Route
          path={url.routes.pages.serviceError()}
          element={
            <Suspense fallback="Loading">
              <LazyErrorPageContainer />
            </Suspense>
          }
        />

        {/* NOT AVAILABLE PAGE */}
        <Route
          path={url.routes.pages.notAvailablePage()}
          element={
            <Suspense fallback="Loading">
              <LazyNotAvailablePage />
            </Suspense>
          }
        />

        {/* BANNED PAGE */}
        <Route
          path={url.routes.pages.banned()}
          element={
            <Suspense fallback="Loading">
              <LazyBannedPage />
            </Suspense>
          }
        />

        {/* LANG START */}
        <Route path=":lang" element={<LanguagePath />}>
          <Route
            element={
              <PublicRoute>
                <CasinoLayout />
              </PublicRoute>
            }
          >
            {CASINO_ROUTES.map((route) => {
              if (PAGES.HOME) {
                return <Route key={route.id} index element={route.page} />;
              }
              if (PAGES.SPORTSBOOK) {
                if (
                  !useFeatureFlag("games_betting_gr8", `${userData?.id}`) &&
                  isDev
                ) {
                  return (
                    <Route
                      key={route.id}
                      path={`/:lang${route.url}`}
                      element={route.page}
                    />
                  );
                }
                return (
                  <Route
                    key={PAGES.HOME}
                    index
                    element={
                      <Suspense fallback="Loading">
                        <LazyHomePageContainer />
                      </Suspense>
                    }
                  />
                );
              }
              return (
                <Route
                  key={route.id}
                  path={`/:lang${route.url}`}
                  element={route.page}
                />
              );
            })}
            {isDesktop && (
              <Route
                path={`/:lang/${url.config.casino.game}`}
                element={
                  <Suspense fallback="Loading">
                    <LazyGamePageContainer />
                  </Suspense>
                }
              />
            )}
          </Route>
          <Route
            element={
              <PrivateRoute>
                <ProfileLayout />
              </PrivateRoute>
            }
          >
            <Route path={`/:lang/${url.config.profile.root}`}>
              {PROFILE_ROUTES.map((route) => {
                const isRoot = route.url.includes(url.config.profile.root);
                return (
                  <Route
                    key={route.id}
                    path={isRoot ? "" : route.url}
                    element={route.page}
                  />
                );
              })}
            </Route>
          </Route>
          <Route
            element={
              <PublicRoute>
                <TournamentsLayout />
              </PublicRoute>
            }
          >
            <Route path={`/:lang/${url.config.tournament.root}`}>
              {TOURNAMENT_ROUTES.map((route) => {
                const isRoot = route.url.includes(url.config.tournament.root);
                return (
                  <Route
                    key={route.id}
                    path={isRoot ? "" : route.url}
                    element={route.page}
                  />
                );
              })}
            </Route>
          </Route>
          {!isDesktop && (
            <Route
              path={`/:lang/${url.config.casino.game}`}
              element={
                <Suspense fallback="Loading">
                  <LazyGamePageContainer />
                </Suspense>
              }
            />
          )}
          <Route
            element={
              <LandingRoute>
                <LandingLayout />
              </LandingRoute>
            }
          >
            <Route path={`/:lang/${url.config.landing.root}`}>
              {LANDING_ROUTES.map((route) => {
                const isRoot = route.url.includes(url.config.landing.root);
                return (
                  <Route
                    key={route.id}
                    path={isRoot ? "" : route.url}
                    element={route.page}
                  />
                );
              })}
            </Route>
          </Route>
          <Route
            element={
              <PublicRoute>
                <InfoLayout />
              </PublicRoute>
            }
          >
            <Route path={`/:lang/${url.config.info.root}`}>
              {INFO_ROUTES.map((route) => {
                const isRoot = route.url.includes(url.config.info.root);
                return (
                  <Route
                    key={route.id}
                    path={isRoot ? "" : route.url}
                    element={route.page}
                  />
                );
              })}
            </Route>
          </Route>
          <Route
            element={
              <PublicRoute>
                <PromoLayout />
              </PublicRoute>
            }
          >
            <Route path={`/:lang/${url.config.promo.root}`}>
              {PROMO_ROUTES.map((route) => {
                const isRoot = route.url.includes(url.config.promo.root);
                return (
                  <Route
                    key={route.id}
                    path={isRoot ? "" : route.url}
                    element={route.page}
                  />
                );
              })}
            </Route>
          </Route>
          <Route
            path={`/:lang${url.routes.pages.serviceError()}}`}
            element={
              <Suspense fallback="Loading">
                <LazyErrorPageContainer />
              </Suspense>
            }
          />
          <Route
            path={`/:lang${url.routes.pages.notAvailablePage()}}`}
            element={
              <Suspense fallback="Loading">
                <LazyNotAvailablePage />
              </Suspense>
            }
          />
          <Route
            path={`/:lang${url.routes.pages.banned()}}`}
            element={
              <Suspense fallback="Loading">
                <LazyBannedPage />
              </Suspense>
            }
          />
          <Route
            path="/:lang/*"
            element={
              <Suspense fallback="Loading">
                <LazyPage404 />
              </Suspense>
            }
          />
        </Route>
        {/* LANG END */}

        {/* 404 */}
        <Route
          path="/404"
          element={
            <Suspense fallback="Loading">
              <LazyPage404 />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Suspense fallback="Loading">
              <LazyPage404 />
            </Suspense>
          }
        />
      </Routes>
      <SubPopupManager />
      <PopupManager />
      <PageManager />
    </>
  );
};

export default App;
